import React from 'react'
import { Box } from '@mui/material'
import BlurOnIcon from '@mui/icons-material/BlurOn'
import CheckIcon from '@mui/icons-material/Check'
import BlurCircularIcon from '@mui/icons-material/BlurCircular'
import DoNotDisturbAltIcon from '@mui/icons-material/DoNotDisturbAlt'

import {
  BACKGROUND_PRESETS,
  NO_BACKGROUND_EFFECT,
  backgroundEffectKey,
  resolveStaticBackgroundImageUrl,
  type BackgroundEffect,
} from '../services/liveKitVideoBackground'

export interface EffectChoice {
  effect: BackgroundEffect
  label: string
  icon: React.ReactNode
}

/**
 * The non-image options, shared by the in-call menu and the pre-join dialog so
 * the two surfaces cannot drift apart on what is on offer.
 */
export const EFFECT_CHOICES: EffectChoice[] = [
  {
    effect: NO_BACKGROUND_EFFECT,
    label: 'No effect',
    icon: <DoNotDisturbAltIcon fontSize="small" />,
  },
  {
    effect: { kind: 'blur', intensity: 'light' },
    label: 'Blur - light',
    icon: <BlurOnIcon fontSize="small" />,
  },
  {
    effect: { kind: 'blur', intensity: 'strong' },
    label: 'Blur - strong',
    icon: <BlurCircularIcon fontSize="small" />,
  },
]

export interface BackgroundPresetGridProps {
  activeKey: string
  onSelect: (effect: BackgroundEffect) => void
  columns?: number
  disabled?: boolean
  maxWidth?: number | string
  /**
   * A tile is about to be picked: pointed at, focused or pressed. Lets the
   * caller start loading its image before the click lands.
   */
  onPrewarm?: (effect: BackgroundEffect) => void
}

/**
 * The bundled background images, as a grid of thumbnails.
 *
 * A grid rather than more list rows because an image is only judgeable by
 * looking at it — the thumbnail has to be the control.
 *
 * Thumbnails are 4:3 to match the in-call video tile (200x150, `object-fit:
 * cover`) rather than the plate's own 16:9. A 16:9 thumbnail would show a third
 * more width than the call ever does, promising content the user never sees.
 */
export const BackgroundPresetGrid: React.FC<BackgroundPresetGridProps> = ({
  activeKey,
  onSelect,
  columns = 3,
  disabled = false,
  maxWidth = 300,
  onPrewarm,
}) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: `repeat(${columns}, 1fr)`,
      gap: 0.75,
      maxWidth,
    }}
  >
    {BACKGROUND_PRESETS.map((preset) => {
      const effect: BackgroundEffect = { kind: 'preset', id: preset.id }
      const key = backgroundEffectKey(effect)
      const selected = key === activeKey
      return (
        <Box
          key={preset.id}
          component="button"
          type="button"
          title={preset.label}
          aria-label={preset.label}
          aria-pressed={selected}
          disabled={disabled}
          data-testid={`background-effect-option-${key}`}
          onClick={() => onSelect(effect)}
          onPointerEnter={onPrewarm && (() => onPrewarm(effect))}
          onFocus={onPrewarm && (() => onPrewarm(effect))}
          onPointerDown={onPrewarm && (() => onPrewarm(effect))}
          sx={{
            position: 'relative',
            p: 0,
            aspectRatio: '4 / 3',
            border: (t) => `2px solid ${selected ? t.palette.primary.main : 'transparent'}`,
            borderRadius: 1,
            overflow: 'hidden',
            cursor: disabled ? 'default' : 'pointer',
            background: 'transparent',
            opacity: disabled ? 0.5 : 1,
            '&:hover': { opacity: disabled ? 0.5 : 0.85 },
          }}
        >
          <Box
            component="img"
            src={resolveStaticBackgroundImageUrl(effect) ?? undefined}
            alt=""
            loading="lazy"
            // Matches the flavour the processor loads with, so the thumbnail and
            // the applied plate are one cache entry rather than two fetches.
            crossOrigin="anonymous"
            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
          {selected && (
            <CheckIcon
              fontSize="small"
              sx={{
                position: 'absolute',
                right: 2,
                bottom: 2,
                color: 'common.white',
                filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.8))',
              }}
            />
          )}
        </Box>
      )
    })}
  </Box>
)

export default BackgroundPresetGrid
